import { useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useLoansList } from 'modules/pools/hooks/useLoansList'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'

import { PageLoader } from 'shared/ui/layout/PageLoader'
import { DashboardRowLoan } from 'modules/pools/ui/DashboardRowLoan'
import { DashboardEmptyCTA } from 'modules/pools/ui/DashboardEmptyCTA'

import {
  ContractRociPayment,
  ContractCollateralManager,
} from 'modules/contracts/contracts'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import { getERCAssetByAddress } from 'modules/pools/constants/poolAssets'
import { createRoute } from 'modules/router/utils/createRoute'
import * as links from 'modules/router/links'
import { isLoanActive } from 'modules/pools/utils/isLoanActive'

function RouteDashboardLoans() {
  const loans = useLoansList()
  const { chainId, walletAddress } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const loansDisplay = useMemo(
    () => loans.data?.filter(({ loan }) => isLoanActive(loan)),
    [loans.data],
  )

  const paymentAddress = ContractRociPayment.chainAddress.get(chainId)
  const swrCollateral = ContractCollateralManager.useSwrRpc(
    Boolean(walletAddress) && 'getCollateralLookup',
    [paymentAddress, walletAddress!],
  )

  const totalOutstanding = useMemo(() => {
    if (!loansDisplay) return
    return loansDisplay.reduce((res, loan) => {
      const { ERC20Address, totalPaymentsValue, paymentComplete } = loan.loan
      const borrowedAsset = getERCAssetByAddress(ERC20Address, chainId)
      const borrowedAssetDecimals = getAssetDecimals(borrowedAsset)
      const totalDebt = weiToNum(
        totalPaymentsValue.sub(paymentComplete),
        borrowedAssetDecimals,
      )
      return res + totalDebt
    }, 0)
  }, [loansDisplay, chainId, getAssetDecimals])

  if (loans.isLoading || swrCollateral.isLoading) {
    return <PageLoader />
  }

  if (!loansDisplay || loansDisplay.length === 0) {
    return (
      <DashboardEmptyCTA
        link={links.borrow}
        fashion="purple"
        timeText="You don’t have"
        entityName="loans"
        actionText="Borrow"
        testId="dashboard-loans-empty-cta"
      />
    )
  }

  return (
    <>
      {loansDisplay.map(loanData => (
        <DashboardRowLoan
          key={loanData.loanId}
          loan={loanData.loan}
          loanId={loanData.loanId}
          isCompleted={loanData.isCompleted}
          totalOutstanding={totalOutstanding!}
          collateralAddress={swrCollateral.data?.[0]!}
          totalCollateralAmount={swrCollateral.data?.[1]!}
        />
      ))}
    </>
  )
}

export const routeDashboardLoans = createRoute({
  component: RouteDashboardLoans,
  checkConnect: true,
})
